import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "../components/AppIcon";
import { Skeleton } from "../components/ui/Skeleton";
import { supabase } from "../lib/supabase";
import { useLanguage } from "../context/LanguageContext";

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isAdmin, isInitializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");

  useEffect(() => { document.title = "Log In | Acebarter"; }, []);

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      const from = (location.state as any)?.from;
      const redirectTo = from
        ? `${from.pathname}${from.search || ''}`
        : isAdmin ? "/admin" : "/dashboard";
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isAdmin, isInitializing, navigate, location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setError("");
    setIsLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err.message || "Invalid email or password.");
      setIsLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError("Enter your email address"); return; }
    setError(""); setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSuccess("Password reset link sent. Check your inbox.");
      setShowForgot(false);
    } catch (err: any) {
      setError(err.message || "Failed to send reset email.");
    } finally { setIsLoading(false); }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-5">
          <Skeleton className="h-7 w-24" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left — Form */}
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="h-16 px-6 flex items-center justify-between flex-shrink-0">
          <button onClick={() => navigate("/")} className="hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="Acebarter" width="100" height="28" className="h-7" />
          </button>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground hidden sm:inline">{t('auth.noAccount')}</span>
            <button onClick={() => navigate("/register")} className="text-accent font-semibold hover:underline">
              {t('auth.register')}
            </button>
          </div>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-bold text-foreground mb-1">
              {showForgot ? t('auth.resetPassword') : t('auth.login')}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              {showForgot
                ? "Enter your email to receive a reset link"
                : t('auth.welcomeBackTo')
              }
            </p>

            {error && (
              <div className="bg-error-muted border border-error/20 rounded-lg px-4 py-3 mb-5 flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}
            {forgotSuccess && (
              <div className="bg-success-muted border border-success/20 rounded-lg px-4 py-3 mb-5 flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <p className="text-sm text-success">{forgotSuccess}</p>
              </div>
            )}

            {showForgot ? (
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.email')}</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all"
                    placeholder="Enter your email"
                  />
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full bg-accent hover:bg-accent-dark text-accent-foreground py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50">
                  {isLoading ? "Sending..." : t('auth.sendResetLink')}
                </button>
                <button type="button" onClick={() => { setShowForgot(false); setError(""); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('auth.backToLogin')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.email')}</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required disabled={isLoading}
                    className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-muted-foreground">{t('auth.password')}</label>
                    <button type="button" onClick={() => { setShowForgot(true); setError(""); setForgotSuccess(""); }}
                      className="text-xs text-accent hover:underline">{t('auth.forgot')}</button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} required disabled={isLoading}
                      className="w-full px-4 py-3 pr-11 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                      placeholder="Enter your password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                      <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full bg-accent hover:bg-accent-dark text-accent-foreground py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {isLoading ? (
                    <><div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" /> Signing in...</>
                  ) : t('auth.login')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Right — Brand panel (desktop only) */}
      <div className="hidden lg:flex w-[420px] bg-accent/5 border-l border-border flex-col items-center justify-center p-12">
        <img src="/favicon.png" alt="Acebarter" width="64" height="64" className="w-16 h-16 mb-6" />
        <h2 className="text-xl font-bold text-foreground text-center mb-3">
          Trade Crypto with Confidence
        </h2>
        <p className="text-sm text-muted-foreground text-center max-w-xs mb-8">
          Access real-time markets, secure wallets, and investment plans — all in one platform.
        </p>
        <div className="space-y-3 w-full max-w-xs">
          {[
            { icon: "Shield", text: "Bank-grade security & encryption" },
            { icon: "LineChart", text: "Live market data & trading" },
            { icon: "TrendingUp", text: "Investment plans with up to 35% ROI" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3 bg-card/50 border border-border rounded-lg px-4 py-3">
              <Icon name={item.icon} size={16} className="text-accent flex-shrink-0" />
              <span className="text-xs text-foreground">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
